import { z } from 'zod';
import { timestamp, nullableArray } from '@/lib/schemas/primitives';

export const userSettingsResponseSchema = z.object({
  language: z.string(),
});
export type UserSettingsResponse = z.infer<typeof userSettingsResponseSchema>;

export const householdMemberSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  isManager: z.boolean(),
});
export type HouseholdMember = z.infer<typeof householdMemberSchema>;

export const householdResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  members: nullableArray(householdMemberSchema),
});
export type HouseholdResponse = z.infer<typeof householdResponseSchema>;

export const driveStatusResponseSchema = z.object({
  drive_configured: z.boolean(),
  email: z.string().optional(),
  connected_at: timestamp.optional(),
  root_folder_id: z.string().optional(),
  root_folder_name: z.string().optional(),
  item_picture_folder_id: z.string().optional(),
  item_picture_folder_name: z.string().optional(),
});
export type DriveStatusResponse = z.infer<typeof driveStatusResponseSchema>;

export const calendarStatusResponseSchema = z.object({
  calendar_configured: z.boolean(),
  email: z.string().optional(),
  connected_at: timestamp.optional(),
  calendar_id: z.string().optional(),
  calendar_name: z.string().optional(),
});
export type CalendarStatusResponse = z.infer<typeof calendarStatusResponseSchema>;

export const calendarListItemSchema = z.object({
  id: z.string(),
  summary: z.string(),
  primary: z.boolean(),
});
export type CalendarListItem = z.infer<typeof calendarListItemSchema>;

export const pushNotificationStatusSchema = z.object({
  enabled: z.boolean(),
  endpoint: z.string().optional(),
  vapidKey: z.string(),
});
export type PushNotificationStatus = z.infer<typeof pushNotificationStatusSchema>;

export const inviteRecordSchema = z.object({
  id: z.number(),
  householdId: z.number(),
  householdName: z.string(),
  email: z.string().nullable(),
  expiresAt: timestamp,
  usedAt: timestamp.nullable(),
  createdByName: z.string(),
  createdAt: timestamp,
});
export type InviteRecord = z.infer<typeof inviteRecordSchema>;

export const createInviteResponseSchema = z.object({
  inviteUrl: z.string(),
});
export type CreateInviteResponse = z.infer<typeof createInviteResponseSchema>;

export const pinStatusResponseSchema = z.object({
  enabled: z.boolean(),
  enabledAt: z.string().optional(),
});
export type PinStatusResponse = z.infer<typeof pinStatusResponseSchema>;

export const pinActionResponseSchema = z.object({
  enabled: z.boolean(),
});
export type PinActionResponse = z.infer<typeof pinActionResponseSchema>;

// Paired Mural kiosk devices (GET /devices). snake_case: the devices API
// serializes its Go structs directly rather than through a camelCase DTO.
export const deviceSchema = z.object({
  id: z.number(),
  name: z.string(),
  last_seen_at: timestamp.nullable(),
  paired_by_user_id: z.number().nullable(),
  paired_by_name: z.string().nullable(),
  created_at: timestamp,
});
export type Device = z.infer<typeof deviceSchema>;

// Mural settings (GET/PUT /settings/mural). groceriesListId is the stored
// choice (null = Automático); effectiveList is the list the Mural actually
// uses: the stored one, else the oldest groceries-kind list, else null.
export const muralListRefSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export const muralSettingsSchema = z.object({
  groceriesListId: z.number().nullable(),
  effectiveList: muralListRefSchema.nullable(),
});
export type MuralSettings = z.infer<typeof muralSettingsSchema>;
